import { useEffect, useMemo, useState } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { CtaButton } from "@/components/cta/CtaButton";
import { SITE_URL } from "@/lib/siteConfig";
import { Copy, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useCanonical } from "@/lib/useCanonical";

/**
 * Energy Gap Report
 * ----------------------------------
 * A public, no-login tool: paste (or generate) two weeks of check-ins,
 * see a plain-English summary of your best window, hardest window,
 * and the one habit most linked to your good days.
 *
 * Illustrative only, this is a wellness tool, not a diagnostic device.
 */

const TITLE = "Energy Gap Report | Myelina Health";
const DESC =
  "Turn two weeks of MS energy check-ins into a plain-English report: your best window, your hardest window, and the habit most linked to your good days. No login required.";

type Day = {
  date: string; // yyyy-mm-dd
  energy: number; // 1-10
  sleep: number; // hours
  stress: number; // 1-10
  brainFog: number; // 1-10
  slept7Plus: boolean;
};

const START_HOURS = [
  { label: "Morning (6–10a)", key: "morning" },
  { label: "Midday (10a–2p)", key: "midday" },
  { label: "Afternoon (2–6p)", key: "afternoon" },
  { label: "Evening (6–10p)", key: "evening" },
] as const;

function seedFrom(str: string) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateSample(seedStr = "myelina-sample-v1"): Day[] {
  const rnd = seedFrom(seedStr);
  const now = new Date();
  const days: Day[] = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const slept = rnd() > 0.35;
    const sleepH = slept ? 6.8 + rnd() * 2 : 4.5 + rnd() * 1.8;
    const stress = 3 + Math.round(rnd() * 6);
    const brainFog = 3 + Math.round(rnd() * 5);
    // Energy correlates with sleep and inversely with stress
    let energyBase = (sleepH - 5) * 0.9 - stress * 0.35 - brainFog * 0.2 + 6.5;
    energyBase += (rnd() - 0.5) * 1.4;
    const energy = Math.max(1, Math.min(10, Math.round(energyBase)));
    days.push({
      date: d.toISOString().slice(0, 10),
      energy,
      sleep: Math.round(sleepH * 10) / 10,
      stress,
      brainFog,
      slept7Plus: sleepH >= 7,
    });
  }
  return days;
}

function analyze(days: Day[]) {
  if (days.length < 7) return null;
  const avg = (arr: number[]) => arr.reduce((s, x) => s + x, 0) / arr.length;

  const overallAvg = avg(days.map((d) => d.energy));
  const highDays = days.filter((d) => d.energy >= overallAvg);
  const lowDays = days.filter((d) => d.energy < overallAvg);

  // Day-of-week windows (best / hardest)
  const dow: Record<number, number[]> = {};
  days.forEach((d) => {
    const w = new Date(d.date + "T00:00:00Z").getUTCDay();
    (dow[w] ??= []).push(d.energy);
  });
  const dowEntries = Object.entries(dow)
    .filter(([, v]) => v.length >= 1)
    .map(([k, v]) => ({ day: Number(k), mean: avg(v) }));
  const bestDow = dowEntries.slice().sort((a, b) => b.mean - a.mean)[0];
  const worstDow = dowEntries.slice().sort((a, b) => a.mean - b.mean)[0];
  const DOW_NAMES = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];

  // Habit link, sleep >= 7h
  const sleptGood = days.filter((d) => d.slept7Plus).map((d) => d.energy);
  const sleptShort = days.filter((d) => !d.slept7Plus).map((d) => d.energy);
  const sleepLift =
    sleptGood.length >= 3 && sleptShort.length >= 3
      ? avg(sleptGood) - avg(sleptShort)
      : null;

  // Low-stress vs high-stress days
  const medianStress = [...days].map((d) => d.stress).sort((a, b) => a - b)[Math.floor(days.length / 2)];
  const lowStressEnergy = days.filter((d) => d.stress <= medianStress).map((d) => d.energy);
  const highStressEnergy = days.filter((d) => d.stress > medianStress).map((d) => d.energy);
  const stressDrag =
    lowStressEnergy.length >= 3 && highStressEnergy.length >= 3
      ? avg(lowStressEnergy) - avg(highStressEnergy)
      : null;

  return {
    n: days.length,
    overallAvg,
    highCount: highDays.length,
    lowCount: lowDays.length,
    bestDow: bestDow ? { name: DOW_NAMES[bestDow.day], mean: bestDow.mean } : null,
    worstDow: worstDow ? { name: DOW_NAMES[worstDow.day], mean: worstDow.mean } : null,
    sleepLift,
    stressDrag,
  };
}

const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const EnergyGap = () => {
  useCanonical({ path: "/energy-gap" });
  const [days, setDays] = useState<Day[]>(() => generateSample());
  const [seed, setSeed] = useState("myelina-sample-v1");
  const result = useMemo(() => analyze(days), [days]);

  useEffect(() => {
    document.title = TITLE;
    setMeta("description", DESC);
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESC, "property");
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}/energy-gap`);
  }, []);

  const regenerate = () => {
    const next = `sample-${Date.now()}`;
    setSeed(next);
    setDays(generateSample(next));
  };

  const copyReport = async () => {
    if (!result) return;
    const lines = [
      `Energy Gap Report (illustrative only. ${result.n} days)`,
      `Average energy: ${result.overallAvg.toFixed(1)} / 10`,
      `Good days: ${result.highCount} · Harder days: ${result.lowCount}`,
      result.bestDow ? `Best window: ${result.bestDow.name} (avg ${result.bestDow.mean.toFixed(1)})` : "",
      result.worstDow ? `Hardest window: ${result.worstDow.name} (avg ${result.worstDow.mean.toFixed(1)})` : "",
      result.sleepLift != null ? `Sleep ≥ 7h → +${result.sleepLift.toFixed(1)} energy on average` : "",
      result.stressDrag != null ? `Higher-stress days → −${result.stressDrag.toFixed(1)} energy on average` : "",
      "",
      `Generated with Myelina Health. ${SITE_URL}/energy-gap`,
    ].filter(Boolean);
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast.success("Report copied", { description: "Paste it into notes, email, or your care-team message." });
    } catch {
      toast.error("Couldn't copy, try selecting the text manually.");
    }
  };

  const maxE = Math.max(...days.map((d) => d.energy));

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-6 sm:px-10 lg:px-16 py-20 max-w-4xl">
        <header className="mb-12">
          <p className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground mb-3">
            Free tool · Illustrative only
          </p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-5">
            Your <span className="italic text-primary">Energy Gap</span>, at a glance.
          </h1>
          <p className="lead-italic text-foreground/80 max-w-2xl">
            Two weeks of check-ins turn into a plain-English report: your best window, your hardest window,
            and the one habit most linked to your good days. Below is a sample dataset, refresh to see how
            the report shifts with different patterns.
          </p>
        </header>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 mb-8">
          <Button
            type="button"
            variant="outline"
            onClick={regenerate}
            className="min-h-11"
            aria-label="Generate a new sample dataset"
          >
            <RefreshCw className="w-4 h-4 mr-2" aria-hidden="true" />
            New sample
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={copyReport}
            className="min-h-11"
            aria-label="Copy report as plain text"
          >
            <Copy className="w-4 h-4 mr-2" aria-hidden="true" />
            Copy report
          </Button>
          <span className="text-xs text-muted-foreground">Seed: {seed.slice(-8)}</span>
        </div>

        {/* Sparkline */}
        <section
          aria-label="14-day energy trend"
          className="rounded-2xl border border-border/60 bg-card p-6 mb-8"
        >
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-xl tracking-tight">Last 14 days</h2>
            <span className="text-xs text-muted-foreground">Energy 1–10</span>
          </div>
          <div className="flex items-end gap-1.5 h-32" role="img" aria-label="Daily energy bars">
            {days.map((d) => {
              const h = Math.max(6, (d.energy / Math.max(maxE, 10)) * 100);
              return (
                <div
                  key={d.date}
                  className="flex-1 rounded-t-sm bg-primary/80 hover:bg-primary transition-colors"
                  style={{ height: `${h}%` }}
                  title={`${d.date}: ${d.energy}/10`}
                />
              );
            })}
          </div>
        </section>

        {/* Report */}
        {result && (
          <section
            aria-label="Plain-English report"
            className="rounded-2xl border border-primary/20 bg-primary/[0.03] p-8 mb-10"
          >
            <div className="flex items-center gap-2 mb-5 text-primary">
              <Sparkles className="w-4 h-4" aria-hidden="true" />
              <span className="text-[11px] tracking-[0.22em] uppercase font-medium">Your report</span>
            </div>

            <ul className="space-y-5 text-[15px] leading-[1.8] text-foreground/90">
              <li>
                Across <strong>{result.n} days</strong>, your average energy sat at{" "}
                <strong>{result.overallAvg.toFixed(1)} / 10</strong>. {result.highCount} good days and{" "}
                {result.lowCount} harder ones.
              </li>
              {result.bestDow && result.worstDow && (
                <li>
                  Your best window tends to be <strong>{result.bestDow.name}</strong> (avg{" "}
                  {result.bestDow.mean.toFixed(1)}), and <strong>{result.worstDow.name}</strong> tend to be
                  the ones to protect (avg {result.worstDow.mean.toFixed(1)}).
                </li>
              )}
              {result.sleepLift != null && result.sleepLift > 0 && (
                <li>
                  Nights with <strong>7+ hours of sleep</strong> came with about{" "}
                  <strong>+{result.sleepLift.toFixed(1)} energy</strong> the next day, on average. That's the
                  single habit most linked to your good days here.
                </li>
              )}
              {result.stressDrag != null && result.stressDrag > 0 && (
                <li>
                  Higher-stress days tracked with roughly{" "}
                  <strong>−{result.stressDrag.toFixed(1)} energy</strong>, a signal to build in a lighter
                  window, not a reason to blame yourself.
                </li>
              )}
              <li className="text-sm text-muted-foreground italic pt-2 border-t border-border/50">
                These patterns tend to move together, they're not a diagnosis and they don't predict any
                single day. A wellness tool, never a medical device.
              </li>
            </ul>
          </section>
        )}

        {/* CTA */}
        <section className="rounded-2xl bg-primary text-primary-foreground p-10 text-center">
          <h2 className="font-display text-3xl md:text-4xl tracking-tight mb-4">
            Want this from <span className="italic">your</span> two weeks?
          </h2>
          <p className="text-primary-foreground/85 mb-6 max-w-lg mx-auto">
            Join the invite queue. When you're in, a real Energy Gap report writes itself from your check-ins, no spreadsheets, no charts to read.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <CtaButton intent="requestAccess" source="energy-gap" size="lg" />
          </div>
        </section>

        <p className="mt-12 text-xs text-muted-foreground text-center">
          Illustrative only. Numbers on this page are generated from a sample dataset for demonstration.
        </p>
      </main>
      <Footer />
    </div>
  );
};

export default EnergyGap;
